import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/common/DataTable";
import { Search, Download, RefreshCw, TrendingUp, CreditCard, AlertTriangle, Plus, Edit, Mail } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BillingHistory } from "@/types/license";
import { useBillingHistory } from "@/hooks/useBillingHistory";
import { useBillingOperations } from "@/hooks/useBillingOperations";
import { Skeleton } from "@/components/ui/skeleton";
import { GenerateInvoiceDialog } from "./GenerateInvoiceDialog";
import { PaymentStatusDialog } from "./PaymentStatusDialog";

export const BillingHistoryTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [isGenerateInvoiceDialogOpen, setIsGenerateInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<BillingHistory | null>(null);
  const [isPaymentStatusDialogOpen, setIsPaymentStatusDialogOpen] = useState(false);

  const { 
    billingHistory, 
    isLoading, 
    refetch,
    getTotalRevenue,
    getOverdueAmount,
    getPendingAmount
  } = useBillingHistory({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    clientId: clientFilter !== 'all' ? clientFilter : undefined
  });

  const {
    sendPaymentReminder,
    generateRecurringInvoices,
    syncBillingFromStripe,
    isSendingReminder,
    isGeneratingRecurring,
    isSyncingFromStripe
  } = useBillingOperations();

  const filteredHistory = billingHistory.filter((item) => {
    const matchesSearch = 
      item.stripe_invoice_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.payment_method?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client_license?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const columns = [
    {
      key: 'invoice_date',
      header: 'Data da Fatura',
      render: (history: any) => format(new Date(history.invoice_date), 'dd/MM/yyyy', { locale: ptBR }),
    },
    {
      key: 'client',
      header: 'Cliente',
      render: (history: any) => history.client_license?.client?.name || 'N/A',
    },
    {
      key: 'modules',
      header: 'Módulos',
      render: (history: any) => {
        const clientName = history.client_license?.client?.name;
        return clientName || 'N/A';
      },
    },
    {
      key: 'amount',
      header: 'Valor',
      render: (history: BillingHistory) => 
        `R$ ${history.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    },
    {
      key: 'status',
      header: 'Status',
      render: (history: BillingHistory) => {
        const statusColors = {
          paid: 'bg-green-100 text-green-800',
          pending: 'bg-yellow-100 text-yellow-800',
          overdue: 'bg-red-100 text-red-800',
          cancelled: 'bg-gray-100 text-gray-800'
        };
        
        const statusLabels = {
          paid: 'Pago',
          pending: 'Pendente',
          overdue: 'Vencido',
          cancelled: 'Cancelado'
        };

        return (
          <Badge className={statusColors[history.status as keyof typeof statusColors]}>
            {statusLabels[history.status as keyof typeof statusLabels]}
          </Badge>
        );
      },
    },
    {
      key: 'due_date',
      header: 'Vencimento',
      render: (history: BillingHistory) => format(new Date(history.due_date), 'dd/MM/yyyy', { locale: ptBR }),
    },
    {
      key: 'paid_date',
      header: 'Data Pagamento',
      render: (history: BillingHistory) => 
        history.paid_date ? format(new Date(history.paid_date), 'dd/MM/yyyy', { locale: ptBR }) : '-',
    },
    {
      key: 'payment_method',
      header: 'Método de Pagamento',
      render: (history: BillingHistory) => history.payment_method || '-',
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (history: BillingHistory) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedInvoice(history);
              setIsPaymentStatusDialogOpen(true);
            }}
          >
            <Edit className="h-3 w-3" />
          </Button>
          {history.status === 'pending' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => sendPaymentReminder(history.id)}
              disabled={isSendingReminder}
            >
              <Mail className="h-3 w-3" />
            </Button>
          )}
        </div>
      ),
    }
  ];

  const monthlyRevenue = getTotalRevenue('month');
  const yearlyRevenue = getTotalRevenue('year');
  const overdueAmount = getOverdueAmount();
  const pendingAmount = getPendingAmount();

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton para métricas */}
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
            </Card>
          ))}
        </div>
        
        {/* Skeleton para filtros */}
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Skeleton para tabela */}
        <Card>
          <CardContent className="p-6">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full mb-2" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas de Faturamento */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              R$ {monthlyRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Últimos 30 dias</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Anual</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              R$ {yearlyRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Últimos 12 meses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendente</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              R$ {pendingAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Aguardando pagamento</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vencido</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              R$ {overdueAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <p className="text-xs text-muted-foreground">Pagamentos vencidos</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por fatura, cliente ou método de pagamento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="paid">Pago</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="overdue">Vencido</SelectItem>
            <SelectItem value="cancelled">Cancelado</SelectItem>
          </SelectContent>
        </Select>

        <Button 
          onClick={() => setIsGenerateInvoiceDialogOpen(true)}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          Nova Fatura
        </Button>

        <Button 
          variant="outline" 
          onClick={() => generateRecurringInvoices()}
          disabled={isGeneratingRecurring}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          {isGeneratingRecurring ? "Gerando..." : "Cobrança Automática"}
        </Button>

        <Button 
          variant="outline" 
          onClick={() => syncBillingFromStripe()}
          disabled={isSyncingFromStripe}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isSyncingFromStripe ? 'animate-spin' : ''}`} />
          {isSyncingFromStripe ? "Sincronizando..." : "Sincronizar Stripe"}
        </Button>

        <Button variant="outline" onClick={() => refetch()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Button>

        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Exportar
        </Button>
      </div>

      {/* Tabela de Histórico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Faturamento</CardTitle>
          <CardDescription>
            {filteredHistory.length} registro(s) encontrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredHistory.length > 0 ? (
            <DataTable 
              columns={columns} 
              data={filteredHistory}
            />
          ) : (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum histórico encontrado</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Ainda não há histórico de faturamento'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <GenerateInvoiceDialog
        open={isGenerateInvoiceDialogOpen}
        onOpenChange={setIsGenerateInvoiceDialogOpen}
      />
      
      <PaymentStatusDialog
        open={isPaymentStatusDialogOpen}
        onOpenChange={setIsPaymentStatusDialogOpen}
        invoice={selectedInvoice}
      />
    </div>
  );
};